import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link to="/login" className="text-primary-600 hover:text-primary-700">
            &larr; Back to Login
          </Link>
        </div>

        <div className="bg-white shadow rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: March 2026</p>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
              <p className="text-gray-600">
                Apartment 5H ("we," "our," or "us") is a private booking application designed for
                family and friends to coordinate access to a shared vacation apartment. This Privacy
                Policy explains how we collect, use, store, and protect your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
              <p className="text-gray-600 mb-3">We collect the following types of information:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Account Information:</strong> Your name and email address when you create or use an account</li>
                <li><strong>Booking Information:</strong> Dates, notes, and other details related to booking requests and approvals</li>
                <li><strong>Authentication Data:</strong> Encrypted password and session information required for secure login</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p className="text-gray-600 mb-3">Your information is used to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Authenticate your identity and provide secure access to the application</li>
                <li>Process and manage apartment booking requests</li>
                <li>Display booking information to other authorized users of the shared apartment</li>
                <li>Send important booking and account notifications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Information Sharing</h2>
              <p className="text-gray-600">
                Your booking information, including your name and reserved dates, is visible to other
                authorized Apartment 5H users so they can coordinate access to the property. We do not
                sell, rent, or share your personal information with third parties for marketing or
                advertising. We do not use your data for cross-app tracking.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Service Providers</h2>
              <p className="text-gray-600 mb-3">
                We use service providers only to operate the app and deliver core functionality.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Cloudflare:</strong> Hosts the application backend and processes the API traffic needed to handle accounts, sessions, and booking data</li>
                <li><strong>Resend:</strong> Processes transactional emails such as booking reminders and receives recipient email addresses plus the email content required to deliver those messages</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Security</h2>
              <p className="text-gray-600">
                We implement appropriate security measures to protect your personal information,
                including encrypted password storage, secure session handling, and HTTPS encryption
                for data transmitted between the app and our production systems. Access to the
                application is restricted to invited users only.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Biometric Data</h2>
              <p className="text-gray-600">
                On supported platforms, optional biometric authentication such as Face ID or Touch ID
                is handled entirely by the device operating system. We do not receive, store, or have
                access to biometric information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Retention</h2>
              <p className="text-gray-600">
                We retain account information and booking history for as long as your account remains
                active or as long as needed to operate the shared booking calendar. If you want your
                account and associated data deleted, contact us using the email address below.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Your Rights</h2>
              <p className="text-gray-600 mb-3">You have the right to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Access your personal information</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your account and associated data</li>
                <li>Withdraw consent for optional features such as biometric login</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Changes to This Policy</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy from time to time. If we make a material change,
                we will update this page and, when appropriate, notify users in the application or by email.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about this Privacy Policy or our data practices, please
                contact us at{' '}
                <a href="mailto:support@5h.hellers.com" className="text-primary-600 hover:text-primary-700">
                  support@5h.hellers.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
